import type { AxiosResponse, AxiosError } from 'axios'
import axios from 'axios'
import qs from 'qs'
import { deepCopy } from '@/utils/util'
import { isFunction } from '@/utils/validate'
import type { GAxiosInstance, GAxiosOptions } from './typings'
import { ContentTypeEnum, RequestEnum } from './typings'
import { AxiosCanceler } from './axiosCancel'

/**
 * @Author      gx12358
 * @DateTime    2023/1/6
 * @lastTime    2023/1/6
 * @description 重新定义axios
 */
export class GAxios {
  private axiosInstance: GAxiosInstance
  private readonly options: GAxiosOptions

  constructor(options: GAxiosOptions) {
    this.options = options
    this.axiosInstance = axios.create(options)
    this.setupInterceptors()
  }

  /**
   * @description: Interceptor configuration 拦截器配置
   */
  private setupInterceptors() {
    const {
      requestInterceptors,
      requestInterceptorsCatch,
      responseInterceptors,
      responseInterceptorsCatch
    } = this.options

    const axiosCanceler = new AxiosCanceler()

    this.axiosInstance.interceptors.request.use((config: GAxiosOptions) => {
      const { ignoreCancelToken } = config
      const ignoreCancel =
        ignoreCancelToken !== undefined
          ? ignoreCancelToken
          : this.options?.ignoreCancelToken

      !ignoreCancel && axiosCanceler.addPending(config)
      if (requestInterceptors && isFunction(requestInterceptors)) {
        config = requestInterceptors(config)
      }
      return config
    }, undefined)

    // Request interceptor error capture
    requestInterceptorsCatch &&
    isFunction(requestInterceptorsCatch) &&
    this.axiosInstance.interceptors.request.use(undefined, requestInterceptorsCatch)

    // Response result interceptor processing
    this.axiosInstance.interceptors.response.use((res: AxiosResponse) => {
      res && axiosCanceler.removePending(res.config)
      if (responseInterceptors && isFunction(responseInterceptors)) {
        return responseInterceptors(res)
      }
      return res
    }, undefined)

    // Response result interceptor error capture
    responseInterceptorsCatch &&
    isFunction(responseInterceptorsCatch) &&
    this.axiosInstance.interceptors.response.use(undefined, (error) => {
      return responseInterceptorsCatch(this.axiosInstance, error)
    })
  }

  // support form-data
  supportFormData(config: GAxiosOptions) {
    const headers = config.headers || this.options.headers
    const contentType = headers?.['Content-Type'] || headers?.['content-type']

    if (
      contentType !== ContentTypeEnum.FORM_URLENCODED ||
      !Reflect.has(config, 'data') ||
      config.method?.toUpperCase() === RequestEnum.GET
    ) {
      return config
    }

    return {
      ...config,
      data: qs.stringify(config.data, { arrayFormat: 'brackets' })
    }
  }

  request<T = ResponseResult | boolean>(config: GAxiosOptions): Promise<T> {
    let conf: GAxiosOptions = deepCopy(config)

    const opt: GAxiosOptions = Object.assign({}, this.options, conf)

    const { beforeRequestHook, requestCatchHook, transformResponseHook } = opt
    if (beforeRequestHook && isFunction(beforeRequestHook)) {
      conf = beforeRequestHook(opt)
    }

    conf = this.supportFormData(opt)

    return new Promise((resolve) => {
      this.axiosInstance
        .request<any, AxiosResponse>(conf)
        .then((res: AxiosResponse) => {
          if (transformResponseHook && isFunction(transformResponseHook)) {
            try {
              const ret = transformResponseHook(res, config)
              resolve(ret)
            } catch (err) {
              resolve(false as any)
              return
            }
            return
          }
          resolve(res as unknown as Promise<T>)
        })
        .catch((e: Error | AxiosError) => {
          if (requestCatchHook && isFunction(requestCatchHook)) {
            resolve(requestCatchHook(e) as any)
            return
          }
          resolve(false as any)
        })
    })
  }
}
